/**
 * @fileoverview The Control Panel extension contract.
 *
 * THIS IS THE REAL SHAPE, NOT AN INVENTED ONE. Foundry's Control Panel is not a
 * page with a switch in it; it is an extension host. Its shipped registry was
 * recovered from the 6.521.57 bundle — 94 definitions, 90 of them lazy — and
 * this interface is that definition, minus the bundle forensics (chunk ids,
 * webpack module ids, source spans) which describe how it was *found* rather
 * than what it *is*.
 *
 *     ../palantir/mcp/foundry/evidence/ui/
 *       control-panel-default-extension-registry-6.521.57.json
 *
 * The packaging is real too. `<feature>-extension/src/components/
 * ControlPanelExtension.tsx` is a literal authored path, leaked through
 * `safeErrorLocation` and recorded in `control-panel-exact-source-paths-
 * 6.521.57.json`: one npm package per settings page, each exporting the same
 * component name. That is why this file defines a CONTRACT and not a union of
 * our own slugs — the host must be able to load a page it has never heard of.
 *
 * WHAT I DELIBERATELY KEPT that a smaller design would drop, because dropping
 * it is what turns an extension host back into a switch statement:
 *
 *   - `type` is the SCOPE a setting belongs to, and it is why the same host
 *     serves an enrollment-wide page and a per-space one. Foundry ships 38
 *     enrollment / 31 organization / 22 space / 3 global.
 *   - conjunctive AND disjunctive operations. Every permission model starts
 *     with "all of these" and acquires "any of these" the first time two roles
 *     may both see a page. Foundry has both; collapsing them costs a rewrite.
 *   - `supersededBy`, so a replaced page can be hidden without deleting the
 *     definition that still owns its URL.
 *   - the dynamic predicates, which are async because availability can depend
 *     on a service answering. That is also why hydration needs a timeout.
 */

/**
 * The scope a setting acts on. Foundry's four, verbatim: a definition declares
 * what kind of thing it configures, and the host resolves the current one from
 * context.
 *
 * We serve `enrollment` (the deployment), `organization` (the agency) and
 * `global` today. `space` is kept because it is in the real contract and a
 * per-space setting is the obvious next scope, not because a page uses it.
 */
export type ExtensionType = "enrollment" | "organization" | "space" | "global";

/**
 * The sidebar/All-Settings grouping. Foundry's nine, with counts from the
 * shipped registry: application-configuration 42, security-governance 17,
 * networking 11, communications-support 6, external-integrations 5,
 * monitoring 4, user-experience 3, aip 3, and 3 with none at all — so the
 * field is genuinely optional and the projection must handle its absence
 * rather than defaulting it into a bucket.
 */
export type ExtensionDisplayGroup =
  | "aip"
  | "application-configuration"
  | "communications-support"
  | "external-integrations"
  | "monitoring"
  | "networking"
  | "security-governance"
  | "user-experience"
  | "personal";

/** Ordered for display; the projection renders groups in this order. */
/*
  RENDER ORDER, AND IT IS ALPHABETICAL BY LABEL — which is what the tenant's All
  Settings shows: AIP, Application Configuration, Communications & Support,
  External Integrations, Monitoring, Networking, Security & Governance, User
  Experience. `personal` is ours and has no upstream counterpart, so it leads;
  a person's own account settings above the platform's is the one ordering
  decision here that is not simply copied.
*/
export const EXTENSION_DISPLAY_GROUPS: readonly ExtensionDisplayGroup[] = [
  "personal",
  "aip",
  "application-configuration",
  "communications-support",
  "external-integrations",
  "monitoring",
  "networking",
  "security-governance",
  "user-experience",
];

/** Labels exactly as the tenant renders them, ampersands and casing included. */
export const EXTENSION_DISPLAY_GROUP_LABELS: Record<ExtensionDisplayGroup, string> = {
  personal: "Personal",
  aip: "AIP",
  "application-configuration": "Application Configuration",
  "communications-support": "Communications & Support",
  "external-integrations": "External Integrations",
  monitoring: "Monitoring",
  networking: "Networking",
  "security-governance": "Security & Governance",
  "user-experience": "User Experience",
};

/**
 * The setup card an extension contributes to the Control Panel home page.
 *
 * Real field, real shape: `presentation.homePage.setup` with `title`,
 * `description` and an icon, recovered as a static object expression on
 * definitions that have one. Ours carries a Blueprint icon name where Foundry
 * inlines an SVG data URI, because we have an icon font and they ship assets.
 */
export interface ExtensionHomePageSetup {
  readonly title: string;
  readonly description: string;
  readonly icon: string;
}

export interface ExtensionPresentation {
  /**
   * Whether the host draws the page header. False on pages that draw their own
   * — `organization-permissions` ships `isHeaderVisible: false` while
   * `enrollment-permissions` ships true, so this is a per-page decision in the
   * real registry and not a constant.
   */
  readonly isHeaderVisible: boolean;
  /** Old URLs that should land on this extension. Empty for most. */
  readonly redirectUrlPaths: readonly string[];
  /** Contribution to the Control Panel home page, when the page has one. */
  readonly homePage?: ExtensionHomePageSetup;
}

/**
 * Everything that decides whether a person may see and use this page.
 *
 * Operations are strings in a `<service>:<subject>:<verb>` shape, matching the
 * real `control-panel:enrollment:view-role-grants` and
 * `organization:view-role-grants`. Ours resolve against Cedar.
 */
export interface ExtensionVisibility {
  /** ALL of these must be held. */
  readonly conjunctiveOperations: readonly string[];
  /** ANY of these suffices. Empty means "no disjunctive requirement". */
  readonly disjunctiveOperations: readonly string[];
  /** Backing services that must be reachable for the page to function. */
  readonly requiredServices: readonly string[];
  /**
   * Async, and therefore timeout-bounded. Returning false hides the page
   * entirely; contrast `dynamicEnablement`, which leaves it visible and locked.
   */
  readonly dynamicDiscoverability?: () => Promise<boolean>;
  readonly dynamicEnablement?: () => Promise<boolean>;
}

export interface ControlPanelExtensionDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly description: string;
  readonly type: ExtensionType;
  /** Path under the Control Panel root, leading slash, e.g. `/roles`. */
  readonly urlPath: string;
  readonly displayGroup?: ExtensionDisplayGroup;
  readonly documentationLink?: string;
  /**
   * Extra terms that should match this page in All Settings' search box beyond
   * its name and description — "SSO" finding Authentication, say. Real field;
   * empty on most definitions.
   */
  readonly keywords: readonly string[];
  /** The id of the extension that replaces this one, or null. */
  readonly supersededBy: string | null;
  readonly presentation: ExtensionPresentation;
  readonly visibility: ExtensionVisibility;
  /**
   * The page itself, lazily. 90 of Foundry's 94 are lazy and the reason is
   * mechanical: the host is one route, so a non-lazy registry would put every
   * settings page in the entry chunk.
   *
   * OPTIONAL, AND THAT IS THE INTERESTING CASE. The registry lists what this
   * platform can configure; a definition with no component is one we have not
   * built. It stays DISCOVERABLE — it appears in All Settings, greyed, behind
   * "Show locked settings" — because an administrator learning the capability
   * exists and is not yet available is better served than one shown a shorter
   * list and left to assume it does not exist at all.
   */
  readonly component?: () => Promise<{ default: React.ComponentType }>;
}

/**
 * A definition after hydration: what the current viewer, on the current
 * deployment, may actually do with it.
 *
 * `isVisible` and `isDiscoverable` are separate on purpose and the difference
 * is the whole point of the "Show locked settings" toggle — recovered
 * behaviour: "All Settings derives locked from isVisible and filters the
 * hydrated state by isDiscoverable, context, display group, and the Show
 * locked settings toggle."
 *
 * So: not discoverable ⇒ absent, as though the page does not exist. Not
 * visible ⇒ present but locked, because telling an administrator a page exists
 * and they lack the operation is more useful than hiding it.
 */
export interface HydratedExtension {
  readonly definition: ControlPanelExtensionDefinition;
  /** False when the definition has no component — listed, not yet built. */
  readonly isImplemented?: boolean;
  readonly isDiscoverable: boolean;
  readonly isVisible: boolean;
  /** Why it is hidden or locked, for the tooltip and for debugging. */
  readonly reason?: string;
}
